import type { RemoteThreadListAdapter } from "@assistant-ui/react";
import type { Client } from "@langchain/langgraph-sdk";
import type { QueryClient } from "@tanstack/react-query";

import {
  AGENT_THREADS_PAGE_SIZE,
  fetchAgentThreads,
  invalidateAgentThreads
} from "@/agents-v2/chat-runtime/thread-list-query";
import {
  asMetadata,
  isArchivedThread,
  isCreationThread,
  mergeThreadMetadata,
  withAgentScope
} from "@/agents-v2/chat-runtime/thread-metadata";
import { threadStateCache } from "@/agents-v2/chat-runtime/thread-state-cache";
import type {
  AgentThreadMetadata,
  AgentThreadSummary,
  AgentThreadValues,
  ThreadMetadataPatcher
} from "@/agents-v2/chat-runtime/types";
import {
  createStoredTitleStream,
  displayThreadTitle
} from "@/agents-v2/title-generation/thread-title";

type RemoteThreadMetadata = Awaited<
  ReturnType<RemoteThreadListAdapter["list"]>
>["threads"][number];

const toThreadMetadata = (t: AgentThreadSummary): RemoteThreadMetadata => {
  const meta = asMetadata(t.metadata);
  return {
    status: isArchivedThread(t.metadata) ? "archived" : "regular",
    remoteId: t.thread_id,
    externalId: t.thread_id,
    // Reads through to Aegra's own first-message stamp when no title has been
    // written yet, so a populated thread can never render as "New chat".
    title: displayThreadTitle(meta),
    // Surface the backend timestamps plus the origin section (stamped at
    // creation) so the version history drawer can group threads by date
    // (Today / Yesterday / older) or by question, and tag rows with their
    // section. assistant-ui carries `custom` straight through to each thread
    // item's state.
    custom: {
      updated_at: t.updated_at,
      created_at: t.created_at,
      section_id: meta.section_id ?? null,
      section_number: meta.section_number ?? null,
      // The thread that built the bid (stamped by the creator), so the io.ai
      // tab can return to it on a browser that did not start it.
      creation: isCreationThread(t.metadata)
    }
  };
};

const isVisibleThread = (t: AgentThreadSummary) => {
  const meta = asMetadata(t.metadata);
  const hidden = meta.hidden === true || meta.hidden === "true";
  return !hidden;
};

export const createThreadListAdapter = (
  client: Client,
  assistantId: string,
  getInitialMetadata: () => AgentThreadMetadata | undefined,
  patchThreadMetadata?: ThreadMetadataPatcher,
  getThreadScopeMetadata: () => AgentThreadMetadata | undefined = () =>
    undefined,
  {
    queryClient
  }: {
    /**
     * Shares the list with everything else that searches these threads on
     * mount (the async-task seeder). Without it every list is its own request.
     */
    queryClient?: QueryClient;
  } = {}
): RemoteThreadListAdapter => {
  /**
   * A write changed what the list would return: mark every cached page of
   * this scope stale so the next reader (a remount, the seeder, the drawer)
   * re-reads instead of serving the pre-write list for up to 30 s.
   */
  const listChanged = async (): Promise<void> => {
    if (!queryClient) return;
    await invalidateAgentThreads(queryClient, {
      assistantId,
      scopeMetadata: getThreadScopeMetadata()
    });
  };

  return {
    async list({ after } = {}) {
      const offset = Number(after ?? 0);
      const limit = AGENT_THREADS_PAGE_SIZE;
      const scopeMetadata = getThreadScopeMetadata();
      const threads = queryClient
        ? await fetchAgentThreads(queryClient, client, {
            assistantId,
            scopeMetadata,
            limit,
            offset
          })
        : await client.threads.search<AgentThreadValues>({
            limit,
            offset,
            metadata: withAgentScope(assistantId, scopeMetadata),
            sortBy: "updated_at",
            sortOrder: "desc"
          });
      const page = threads.filter(isVisibleThread).map(toThreadMetadata);

      return {
        threads: page,
        nextCursor:
          threads.length === limit ? String(offset + limit) : undefined
      };
    },
    async initialize() {
      const t = await client.threads.create({
        metadata: withAgentScope(assistantId, {
          ...getInitialMetadata(),
          ...getThreadScopeMetadata()
        })
      });
      await listChanged();
      return { remoteId: t.thread_id, externalId: t.thread_id };
    },
    async fetch(threadId) {
      const record = await threadStateCache.getThreadRecord(client, threadId);
      return toThreadMetadata({
        thread_id: threadId,
        ...record
      } as AgentThreadSummary);
    },
    async rename(remoteId, title) {
      await (patchThreadMetadata?.(remoteId, { title }) ??
        mergeThreadMetadata(client, remoteId, assistantId, { title }));
      await listChanged();
    },
    async archive(remoteId) {
      await (patchThreadMetadata?.(remoteId, { archived: true }) ??
        mergeThreadMetadata(client, remoteId, assistantId, { archived: true }));
      await listChanged();
    },
    async unarchive(remoteId) {
      await (patchThreadMetadata?.(remoteId, { archived: false }) ??
        mergeThreadMetadata(client, remoteId, assistantId, {
          archived: false
        }));
      await listChanged();
    },
    async delete(remoteId) {
      await client.threads.delete(remoteId);
      threadStateCache.invalidate(remoteId);
      await listChanged();
    },
    /**
     * Read back the name the agent gave this thread; never generate one.
     *
     * assistant-ui calls this from a `runEnd` listener in the tab, which our
     * server-side runs do not reliably reach — see `createStoredTitleStream`.
     * @param remoteId Thread whose stored name to read.
     * @returns A stream carrying that name.
     */
    async generateTitle(remoteId) {
      return createStoredTitleStream({ client, remoteId });
    }
  };
};
